import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BackendApiError, publicPassportApi } from '../../services/backendApi';
import type { PublicPassportLive } from '../../types';
import { PassportProfileCard } from './PassportProfileCard';
import { PublicPassportShell } from './PublicPassportShell';

export const PublicPassportView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [live, setLive] = useState<PublicPassportLive | null>(null);

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    publicPassportApi
      .getByToken(token)
      .then(data => {
        if (cancelled) return;
        if (!data.visible) {
          setHidden(true);
        } else {
          setLive(data);
        }
      })
      .catch(err => {
        if (cancelled) return;
        if (err instanceof BackendApiError && err.status === 404) {
          setNotFound(true);
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return <PublicPassportShell loading />;
  }

  if (notFound) {
    return (
      <PublicPassportShell
        error
        title="Passport not found"
        message="This Passport link is invalid. Please check the QR code or ask the owner for a new card."
      />
    );
  }

  if (hidden || !live) {
    return (
      <PublicPassportShell
        title="This Passport is currently hidden"
        message="This Passport is currently hidden. Ask the owner to make it visible."
      />
    );
  }

  return (
    <PassportProfileCard
      fields={live.selected_fields}
      profile={live.profile}
      workExperience={live.work_experience ?? []}
      qualifications={live.qualifications ?? []}
      references={live.references ?? []}
      documents={live.documents ?? []}
      bannerRight="Passport"
      footerLeft="This is a live Passport page"
    />
  );
};
